/**
 * <ec-mega-menu>
 *
 * Desktop nav with optional mega-panels. Hover-intent, ARIA, keyboard.
 * Mobile (≤1023px) hides the whole component via CSS — drawer carries
 * a flat nav.
 *
 * Focus-trap policy: the trap is activated only when a panel is opened
 * via keyboard (ArrowDown on the trigger or focus-in from Tab). Pointer
 * hover/click keeps the rest of the page tabbable so mouse + keyboard
 * hybrid users can still reach the cart, search or skip-link without
 * being forced through the open mega-panel.
 */

import { trapFocus } from '@ec/focus-trap';

const DESKTOP_QUERY = '(min-width: 1024px)';

class EcMegaMenu extends HTMLElement {
  /** @type {Map<string, { open: boolean, openTimer: number|null, closeTimer: number|null, item: HTMLElement, link: HTMLElement, panel: HTMLElement|null, releaseTrap: (() => void) | null }>} */
  #panels = new Map();
  #onPointerEnter = null;
  #onHostPointerEnter = null;
  #onHostPointerLeave = null;
  #onFocusIn = null;
  #onKeydown = null;
  #onDocumentClick = null;
  #onBackdropClick = null;
  #mql = null;
  /** @type {HTMLElement|null} */ #hoverHost = null;
  /** @type {HTMLElement|null} */ #backdrop = null;
  #hoverCloseMs = 300;

  connectedCallback() {
    this.#mql = window.matchMedia(DESKTOP_QUERY);
    this.#hoverHost = this.closest('ec-header') || this;
    this.#backdrop = document.querySelector('.ec-mega-backdrop');
    this.#scanItems();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#panels.forEach((entry) => {
      if (entry.openTimer !== null) clearTimeout(entry.openTimer);
      if (entry.closeTimer !== null) clearTimeout(entry.closeTimer);
      // Release any active trap so its document-level keydown/focusin
      // listeners do not survive a section re-render with an open panel.
      if (entry.releaseTrap) {
        entry.releaseTrap();
        entry.releaseTrap = null;
      }
    });
    this.#panels.clear();
    if (this.#onDocumentClick) {
      document.removeEventListener('click', this.#onDocumentClick);
    }
    if (this.#hoverHost && this.#onHostPointerEnter) {
      this.#hoverHost.removeEventListener('pointerenter', this.#onHostPointerEnter);
    }
    if (this.#hoverHost && this.#onHostPointerLeave) {
      this.#hoverHost.removeEventListener('pointerleave', this.#onHostPointerLeave);
    }
    if (this.#backdrop && this.#onBackdropClick) {
      this.#backdrop.removeEventListener('click', this.#onBackdropClick);
    }
    delete document.body.dataset.ecMegaOpen;
  }

  /**
   * @param {string} handle
   * @param {{ source?: 'pointer' | 'keyboard' }} [opts]
   */
  openPanel(handle, opts = {}) {
    const entry = this.#panels.get(handle);
    if (!entry || entry.open) return;
    // A panel already on screen means the container stays put and only
    // its content changes: the open/close animation must not replay, or
    // browsing from one aisle to the next reads as flicker.
    const switching = this.#firstOpenHandle() !== null;
    // A panel still fading out from a previous close would otherwise
    // overlap the incoming one and read as a cross-fade. Snap it to its
    // end state. The panel being (re)opened is left alone on purpose so
    // an interrupted close reverses smoothly instead of restarting.
    this.#panels.forEach((other, otherHandle) => {
      if (otherHandle === handle || !other.panel) return;
      other.panel.getAnimations().forEach((animation) => animation.finish());
    });
    if (switching) this.dataset.switching = 'true';
    // Mark the incoming panel open BEFORE closing the previous one so
    // closePanel(prev) → #firstOpenHandle() still finds an open entry
    // and keeps the body flag / backdrop alive across the switch.
    entry.open = true;
    document.body.dataset.ecMegaOpen = 'true';
    this.closePanels({ except: handle });
    if (entry.panel) {
      entry.panel.hidden = false;
      entry.panel.setAttribute('aria-hidden', 'false');
    }
    entry.link.setAttribute('aria-expanded', 'true');
    entry.item.dataset.open = 'true';
    if (switching) {
      // Force style resolution while transitions are still suppressed so
      // both panels commit their new state untweened; clearing the flag
      // afterwards changes no value, so nothing starts animating and the
      // next genuine open still fades in.
      if (entry.panel) void entry.panel.offsetHeight;
      delete this.dataset.switching;
    }
    // Focus trap only when opened via keyboard — pointer-driven hover
    // must keep the rest of the page tabbable for mouse + keyboard users.
    if (opts.source === 'keyboard' && entry.panel) {
      entry.releaseTrap = trapFocus(entry.panel);
    }
    // Pin the backdrop to the bottom edge of the header so the dim layer
    // starts right under the open panel (announcement bar + header stay lit).
    if (this.#backdrop && this.#hoverHost) {
      const bottom = this.#hoverHost.getBoundingClientRect().bottom;
      this.#backdrop.style.setProperty('--ec-mega-backdrop-top', `${Math.max(0, bottom)}px`);
    }
    this.dispatchEvent(new CustomEvent('ec:mega:open', { bubbles: true, detail: { handle } }));
  }

  closePanel(handle) {
    const entry = this.#panels.get(handle);
    if (!entry || !entry.open) return;
    entry.open = false;
    if (entry.releaseTrap) {
      entry.releaseTrap();
      entry.releaseTrap = null;
    }
    if (entry.panel) {
      entry.panel.hidden = true;
      entry.panel.setAttribute('aria-hidden', 'true');
    }
    entry.link.setAttribute('aria-expanded', 'false');
    entry.item.removeAttribute('data-open');
    if (!this.#firstOpenHandle()) {
      delete document.body.dataset.ecMegaOpen;
    }
    this.dispatchEvent(new CustomEvent('ec:mega:close', { bubbles: true, detail: { handle } }));
  }

  closePanels({ except } = {}) {
    this.#panels.forEach((_entry, handle) => {
      if (handle !== except) this.closePanel(handle);
    });
  }

  #scanItems() {
    const items = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.ec-mega-menu__item'))
    );
    items.forEach((item) => {
      const link = item.querySelector('.ec-mega-menu__link');
      const panel = item.querySelector('.ec-mega-menu__panel');
      if (!(link instanceof HTMLElement)) return;
      const handle = link.dataset.handle;
      if (!handle) return;
      if (panel instanceof HTMLElement) {
        panel.setAttribute('aria-hidden', 'true');
      }
      this.#panels.set(handle, {
        open: false,
        openTimer: null,
        closeTimer: null,
        item,
        link,
        panel: panel instanceof HTMLElement ? panel : null,
        releaseTrap: null,
      });
    });
  }

  #bindEvents() {
    const hoverOpenMs = Math.max(0, Number(this.dataset.hoverOpenMs) || 150);
    this.#hoverCloseMs = Math.max(0, Number(this.dataset.hoverCloseMs) || 300);

    this.#onPointerEnter = (event) => {
      if (!this.#mql?.matches) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const item = target.closest('.ec-mega-menu__item');
      if (!(item instanceof HTMLElement)) return;
      const handle = this.#handleOf(item);
      if (!handle || !this.#panels.get(handle)?.panel) return;
      this.#scheduleOpen(handle, hoverOpenMs);
    };

    // Host-level pointer tracking: pointerenter/leave are non-bubbling
    // and (when bound without capture) fire only when the pointer
    // genuinely crosses the host's outer hit-test boundary — which
    // includes out-of-flow descendants like the absolute-positioned
    // mega panel. So moving link → padding → panel → column does NOT
    // re-enter; only leaving the whole header does.
    this.#onHostPointerEnter = () => {
      if (!this.#mql?.matches) return;
      this.#panels.forEach((_entry, handle) => this.#cancelClose(handle));
    };

    this.#onHostPointerLeave = () => {
      if (!this.#mql?.matches) return;
      const openHandle = this.#firstOpenHandle();
      if (openHandle) this.#scheduleClose(openHandle, this.#hoverCloseMs);
    };

    this.#onBackdropClick = () => {
      this.closePanels();
    };

    this.#onFocusIn = (event) => {
      if (!this.#mql?.matches) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const item = target.closest('.ec-mega-menu__item');
      if (!(item instanceof HTMLElement)) {
        this.closePanels();
        return;
      }
      const handle = this.#handleOf(item);
      if (!handle) return;
      this.#cancelClose(handle);
      const entry = this.#panels.get(handle);
      if (entry?.panel && !entry.open) this.openPanel(handle, { source: 'keyboard' });
    };

    this.#onKeydown = (event) => {
      if (event.key === 'Escape') {
        const openHandle = this.#firstOpenHandle();
        if (openHandle) {
          event.preventDefault();
          this.closePanel(openHandle);
          this.#panels.get(openHandle)?.link.focus();
        }
        return;
      }
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.classList.contains('ec-mega-menu__link')) return;
      const handle = target.dataset.handle;
      if (!handle) return;

      const items = Array.from(this.querySelectorAll('.ec-mega-menu__link'));
      const currentIdx = items.indexOf(target);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.openPanel(handle, { source: 'keyboard' });
          this.#focusFirstInPanel(handle);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.#focusItem(items[(currentIdx + 1) % items.length]);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.#focusItem(items[(currentIdx - 1 + items.length) % items.length]);
          break;
      }
    };

    this.#onDocumentClick = (event) => {
      if (!(event.target instanceof Node)) return;
      if (!this.contains(event.target)) {
        this.closePanels();
      }
    };

    this.addEventListener('pointerenter', this.#onPointerEnter, true);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    if (this.#hoverHost) {
      this.#hoverHost.addEventListener('pointerenter', this.#onHostPointerEnter);
      this.#hoverHost.addEventListener('pointerleave', this.#onHostPointerLeave);
    }
    if (this.#backdrop) {
      this.#backdrop.addEventListener('click', this.#onBackdropClick);
    }
  }

  #handleOf(item) {
    const link = item.querySelector('.ec-mega-menu__link');
    return link instanceof HTMLElement ? link.dataset.handle || null : null;
  }

  #scheduleOpen(handle, ms) {
    const entry = this.#panels.get(handle);
    if (!entry) return;
    this.#cancelClose(handle);
    if (entry.open) return;
    if (entry.openTimer !== null) clearTimeout(entry.openTimer);
    entry.openTimer = window.setTimeout(() => {
      entry.openTimer = null;
      this.openPanel(handle, { source: 'pointer' });
    }, ms);
  }

  #scheduleClose(handle, ms) {
    const entry = this.#panels.get(handle);
    if (!entry) return;
    if (entry.openTimer !== null) {
      clearTimeout(entry.openTimer);
      entry.openTimer = null;
    }
    if (!entry.open) return;
    if (entry.closeTimer !== null) clearTimeout(entry.closeTimer);
    entry.closeTimer = window.setTimeout(() => {
      entry.closeTimer = null;
      this.closePanel(handle);
    }, ms);
  }

  #cancelClose(handle) {
    const entry = this.#panels.get(handle);
    if (entry?.closeTimer !== null && entry?.closeTimer !== undefined) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
  }

  #firstOpenHandle() {
    for (const [handle, entry] of this.#panels) {
      if (entry.open) return handle;
    }
    return null;
  }

  #focusFirstInPanel(handle) {
    const entry = this.#panels.get(handle);
    if (!entry?.panel) return;
    const focusable = entry.panel.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  #focusItem(el) {
    if (el instanceof HTMLElement) el.focus();
  }
}

if (!customElements.get('ec-mega-menu')) {
  customElements.define('ec-mega-menu', EcMegaMenu);
}
